import React, {
  FC,
  useCallback,
  useMemo,
}                                   from 'react';
import { FormikHelpers }            from 'formik';
import {
  Typography,
  FormGroup,
  Box,
}                                   from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/styles';
import { Button }                   from '@components/Button/Button';
import { CheckboxField }            from './CheckboxField';

const BtnWrapper = withStyles(() => createStyles({
  root: {
    display        : 'flex',
    alignItems     : 'end',
    justifyContent : 'center',
  },
}))(Box);

const SELECT_LABEL   = 'Select All';
const DESELECT_LABEL = 'Clear selections';

export interface ICheckboxOption<TName = string> {
  label       : string;
  value       : boolean;
  name        : TName;
  isSelected? : boolean;
}

interface ICheckboxFieldGroup {
  bulkLabel?    : string;
  groupName     : string;
  groupLabel?   : string;
  options       : ICheckboxOption[];
  row?          : boolean;
  values        : Record<string, boolean>;
  setFieldValue : FormikHelpers<ICheckboxOption[]>['setFieldValue'];
}

export const CheckboxFieldGroup: FC<ICheckboxFieldGroup> = ({
  bulkLabel,
  groupName,
  groupLabel,
  options,
  row,
  values,
  setFieldValue,
}) => {
  const label = useMemo(() => {
    if (Object.values(values).every((el) => !!el)) {
      return DESELECT_LABEL;
    }

    return SELECT_LABEL;
  }, [values]);

  const setBulkFormValue = useCallback(
    (value) => Object.values(options).forEach(
      (item) => setFieldValue(`${groupName}.${item.name}`, value),
    ),
    [groupName, options, setFieldValue],
  );

  const onSelectAll = useCallback(
    () => {
      if (label === SELECT_LABEL) {
        setBulkFormValue(true);
      } else {
        setBulkFormValue(false);
      }
    },
    [label, setBulkFormValue],
  );

  return (
    <>
      {bulkLabel && (
        <BtnWrapper>
          <h2>{bulkLabel}  |</h2>

          <Button
            variant = "text"
            width   = "50px"
            type    = "button"
            label   = {label}
            onClick = {onSelectAll}
          />
        </BtnWrapper>
      )}

      {groupLabel && (
        <Typography
          variant   = "h4"
          component = "p"
        >
          {groupLabel}
        </Typography>
      )}

      <FormGroup row={row}>
        {options.map(({ name, ...option }) => (
          <CheckboxField
            {...option}
            name = {`${groupName}.${name}`}
            key  = {name}
          />
        ))}
      </FormGroup>
    </>
  );
};
